import { getSandbox } from "@/gen_src/proto/chalk/sandbox/v2/service-SandboxService_connectquery";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { useQuery } from "@connectrpc/connect-query";
import { useMemo } from "react";

const NO_BREADCRUMB_LABELS: Readonly<Record<string, string>> = {};

/**
 * Breadcrumb labels for sandbox detail routes whose URL param is an opaque id.
 * The label is "" until GetSandbox loads, which suppresses the raw id; once
 * loaded, an unnamed sandbox falls back to its id.
 *
 * Snapshots (`containerSnapshotId`) have no name field and scaling-group URLs
 * already carry the name, so neither needs a label here.
 */
export const useComputeBreadcrumbLabels = (): Readonly<Record<string, string>> => {
  // The params only exist in the URL on their detail routes, so presence is
  // the route check.
  const { sandboxId } = useRouteInfo();
  // Same request as the detail page's own GetSandbox query, so this reads
  // from the shared cache instead of issuing a second fetch.
  const sandboxQuery = useQuery(getSandbox, { id: sandboxId }, { enabled: sandboxId != null });
  const sandbox = sandboxQuery.data?.sandbox;
  const label = sandbox == null ? "" : sandbox.name || sandbox.id;
  return useMemo(() => {
    if (sandboxId != null) {
      return { sandboxId: label };
    }
    return NO_BREADCRUMB_LABELS;
  }, [sandboxId, label]);
};
